import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { RouteComponentProps } from 'react-router';

import {
  mergeApplication,
  selectCreditApplication,
} from 'state/creditApplication';

import useWorkflow, { Step } from 'hooks/useWorkflow';
import useQueryParameters from 'hooks/useQueryParameters';
import catchOnly from 'utils/catchOnly';
import { NonClientError } from 'hooks/requests/fetchUtils';
import LoadingPage from 'components/LoadingPage';
import useNavigationHelpers from 'hooks/useNavigationHelpers';
import useInitialUnifiedOffers, {
  UnifiedInitialOffersError,
} from 'hooks/requests/unifiedInitialOffers';
import { selectTenant, isOfferConfigured } from 'state/tenant';
import { setExpandedPanel } from 'state/unifiedWidgets';
import { useHasInitialOffers } from '../hasOffers';

import useAnalyticVariables from 'hooks/useAnalyticVariables';

const UnifiedPrequalHome: React.FC<RouteComponentProps> = () => {
  const { firstOffers } = useQueryParameters();
  const { setAnalyticsVariables } = useAnalyticVariables();

  const { push } = useNavigationHelpers();
  const { pathAfter } = useWorkflow();
  const dispatch = useDispatch();
  const { bread } = useSelector(selectTenant);

  if (!bread) {
    // Should never happens
    throw new Error('Invalid tenant');
  }

  const [getInitialOffers] = useInitialUnifiedOffers();
  const getHasInitialOffers = useHasInitialOffers();

  const { intendedPurchaseAmount } = useSelector(selectCreditApplication);
  useEffect(() => {
    const newIntendedPurchaseAmount =
      intendedPurchaseAmount === null
        ? bread.defaultCartAmount.toString()
        : intendedPurchaseAmount;

    if (newIntendedPurchaseAmount !== intendedPurchaseAmount) {
      dispatch(
        mergeApplication({
          intendedPurchaseAmount: newIntendedPurchaseAmount,
        }),
      );
    }

    setAnalyticsVariables({
      initialIntendedPurchaseAmount: newIntendedPurchaseAmount.replace(
        /[^.|\d]/g,
        '',
      ),
    });
  }, [
    bread.defaultCartAmount,
    dispatch,
    intendedPurchaseAmount,
    setAnalyticsVariables,
  ]);

  const loadData = useCallback(async () => {
    let error = false;
    const initialOffers =
      isOfferConfigured('installments', bread) ||
      isOfferConfigured('splitpay', bread)
        ? await getInitialOffers().catch(e => {
            if (catchOnly(e, NonClientError, UnifiedInitialOffersError)) {
              push('/unified/offer-intro/error');
              error = true;
            }
            throw e;
          })
        : undefined;

    const hasInitialOffers = getHasInitialOffers(initialOffers, firstOffers);

    if (hasInitialOffers.length === 1) {
      dispatch(setExpandedPanel(hasInitialOffers[0]));
    } else if (firstOffers) {
      dispatch(setExpandedPanel(firstOffers));
    }

    if (!error) push(pathAfter(Step.UNIFIED_PREQUAL_HOME));
  }, [
    bread,
    getInitialOffers,
    getHasInitialOffers,
    firstOffers,
    push,
    pathAfter,
    dispatch,
  ]);

  useEffect(() => {
    loadData();

    // Ensure we only sent this once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <LoadingPage headerTitle="Just a moment." />;
};

export default UnifiedPrequalHome;
